import { Component, ComponentRef, Inject, OnInit } from '@angular/core';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';

import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ProgressSpinnerComponent } from '../../common/progress-spinner/progress-spinner.component';

import { ToastrService } from 'ngx-toastr';

import { AngularFirestore, AngularFirestoreDocument } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';
import { ReflectDataSchedule } from '../../firestore-models/reflect-data-schedule';

import { ApiReflectDataRequest, ApiReflectDataSchedule, ApiSetReflectDataScheduleRequest } from '../../api-client-module';

import { MessageDialogComponent } from '../../common/message-dialog/message-dialog.component';

import { SpinnerService } from '../../services/spinner.service';
import { ApiClientService } from '../../api-client.service';
import { AppConst } from '../../app-const';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';

import * as moment from 'moment-timezone';


export interface ReflectDataDialogData {
  projectId: string;
  facilityId: string;
}

@Component({
  selector: 'app-reflect-data-dialog',
  templateUrl: './reflect-data-dialog.component.html',
  styleUrls: ['./reflect-data-dialog.component.css'],
  standalone: false,
  providers: [
    // このコンポーネントとその子要素専用のDateAdapterインスタンスを作成 //(0703)
    { provide: DateAdapter, useClass: NativeDateAdapter },
  ]
})
export class ReflectDataDialogComponent implements OnInit {

  private overlayRef!: OverlayRef;
  spinner: ComponentRef<ProgressSpinnerComponent> | undefined;

  private projectId: string;
  private facilityId: string;

  private scheduleDocument!: AngularFirestoreDocument<ReflectDataSchedule>;
  scheduleObservable!: Observable<ReflectDataSchedule>;
  scheduleSubscribe!: Subscription;
  schedule!: ReflectDataSchedule;
  reserveDate!: Date;
  showSeconds = false;
  reflectStatus: string = '';

  reflectStatusDict: any = {
    0: this.translate.instant('未設定'),
    1: this.translate.instant('予約済み'),
    2: this.translate.instant('実行中'),
    9: this.translate.instant('反映失敗')
  };

  isLoad = false;
  formGroup!: FormGroup;
  reserveDateControl!: FormControl;

  currentLang: string = 'ja';
  private langChangeSub!: Subscription; // TranslateServiceの購読を管理

  constructor(
    public dialogRef: MatDialogRef<ReflectDataDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReflectDataDialogData,
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    protected overlay: Overlay,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dateAdapter: DateAdapter<any>
  ) {
    moment.tz.setDefault('Asia/Tokyo');
    this.projectId = this.data.projectId;
    this.facilityId = this.data.facilityId;
  }

  ngOnInit(): void {
    this.createForm();
    this.reserveDateControl = this.formGroup.get('reserveDate') as FormControl;
    this.scheduleDocument = this.angularFirestore.doc<ReflectDataSchedule>(
      `${AppConst.FIRESTORE_SCHEDULE}/${this.projectId}:${this.facilityId}`);
    this.currentLang = this.translate.currentLang; //(0703)
console.log('currentLang', this.currentLang);
    // 1. コンポーネント初期化時に現在の言語でカレンダーを設定
    this.setCalendarLocale(this.currentLang);

    // 2. アプリケーションの言語が変更されたら、カレンダーの言語も動的に追従させる
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.setCalendarLocale(event.lang);
    });

    this.scheduleSubscribe = this.scheduleDocument.valueChanges().subscribe(schedule => {
      if (!!schedule) {
        this.schedule = schedule;
      } else {
        this.schedule = {
          project_id: this.projectId,
          facility_id: this.facilityId,
          status: AppConst.REFLECT_STATUS_NO_RESERVATION,
        }
      }
      this.reflectStatus = this.reflectStatusDict[this.schedule.status];
      this.isLoad = true;
    });
  }
 
  /**
  * Datepickerのロケールを設定するプライベートメソッド
  * @param lang 設定したい言語コード (例: 'en', 'ja')
  */
  private setCalendarLocale(lang: string): void {
    // 言語に応じたロケールを設定
    const locale = lang === 'en' ? 'en-US' : 'ja-JP';
    this.dateAdapter.setLocale(locale);
  }

  createForm() {
    this.formGroup = this.formBuilder.group(
      {
        reserveDate: ['', []],
      }
    );
  }

  cancel() {
    this.dialogRef.close();
  }

  // 本番反映スケジュール設定
  setSchedule(): void {
    let scheduling = true;
    // 予約日時のチェック
    const now = moment(new Date).toDate();
    if (!this.reserveDateControl.value) {
      this.toastr.error(this.translate.instant('日時を設定してください。'));
      scheduling = false;
    } else if (now > this.reserveDateControl.value) {
      this.toastr.error(this.translate.instant('過去の日時は設定できません。'));
      scheduling = false;
    } else {
      // ステータスチェック
      if (this.schedule.status == 2) {
        this.toastr.error(this.translate.instant('過去の日時は設定できません。'));
        scheduling = false;
      }
    }
    // 秒を00にする
    const year = this.reserveDateControl.value.toDate().getFullYear();
    const month = this.reserveDateControl.value.toDate().getMonth();
    const date = this.reserveDateControl.value.toDate().getDate();
    const hour = this.reserveDateControl.value.toDate().getHours();
    const min = this.reserveDateControl.value.toDate().getMinutes();
    const reserveDate = moment(new Date(year, month, date, hour, min)).toDate();
    if (scheduling) {
      const dialogRef = this.dialog.open(MessageDialogComponent, {
        width: '420px',
        height: '180px',
        disableClose: true,
        data: { message: this.translate.instant('本番反映スケジュールを設定します。よろしいですか？') }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (!!result) {
          const schedule: ApiReflectDataSchedule = {
            id: `${this.projectId}:${this.facilityId}`,
            project_id: this.projectId,
            facility_id: this.facilityId,
            reserve_date: reserveDate,
            status: AppConst.REFLECT_STATUS_RESERVED
          }
          let request: ApiSetReflectDataScheduleRequest = {
            access_token: '',
            schedule: schedule
          }
          // this.openSpinner();
          this.spinnerService.openSpinner();
          this.apiClientService.setReflectDataSchedule(request).then(
            response => {
              if (response!.ret === AppConst.API_RET_SUCCESS) {
                this.toastr.success(this.translate.instant('本番反映のスケジュール設定が完了しました。'));
              } else {
                this.toastr.error(this.translate.instant('本番反映のスケジュール設定に失敗しました。'));
              }
            }
          ).catch(
            error => {
              console.log(error);
              this.toastr.error(this.translate.instant('本番反映のスケジュール設定に失敗しました。'));
            }
          ).finally(
            () => {
              // this.closeSpinner();
              this.spinnerService.closeSpinner();
            }
          );
        }
      });
    }
  }

  // 予約取り消し
  cancelSchedule(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('本番反映スケジュールを取り消します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        const schedule: ApiReflectDataSchedule = {
          id: `${this.projectId}:${this.facilityId}`,
          project_id: this.projectId,
          facility_id: this.facilityId,
          status: AppConst.REFLECT_STATUS_NO_RESERVATION
        }
        let request: ApiSetReflectDataScheduleRequest = {
          access_token: '',
          schedule: schedule
        }
        // this.openSpinner();
        this.spinnerService.openSpinner();
        this.apiClientService.setReflectDataSchedule(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('reflect-data-dialog.CANCELED_REFLECT_SCHEDULE'));
            } else {
              this.toastr.error(this.translate.instant('reflect-data-dialog.CANCELED_REFLECT_SCHEDULE_FAIL'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('reflect-data-dialog.CANCELED_REFLECT_SCHEDULE_FAIL'));
          }
        ).finally(
          () => {
            // this.closeSpinner();
            this.spinnerService.closeSpinner();
          }
        );
      }
    });
  }

  // データ本番反映
  reflectData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('reflect-data-dialog.REFLECT_DATA_CONFIRM') }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        let request: ApiReflectDataRequest = {
          access_token: '',
          project_id: this.projectId,
          facility_id: this.facilityId,
          revert_flag: AppConst.REVERT_FLAG,
        }
        // this.openSpinner();
        this.spinnerService.openSpinner();
        this.apiClientService.reflectData(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('reflect-data-dialog.REFLECT_DATA_DONE'));
            } else {
              this.toastr.error(this.translate.instant('reflect-data-dialog.REFLECT_DATA_FAIL'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('reflect-data-dialog.REFLECT_DATA_FAIL'));
          }
        ).finally(
          () => {
            // this.closeSpinner();
            this.spinnerService.closeSpinner();
          }
        );
      }
    });
  }

  // public openSpinner() {
  //   const portal = new ComponentPortal(ProgressSpinnerComponent);

  //   this.overlayRef = this.overlay.create({
  //     width: '100%',
  //     height: '100%',
  //     hasBackdrop: true,
  //     panelClass: 'progress-spinner',
  //     backdropClass: 'progress-spinner-backdrop',
  //   });
  //   this.spinner = this.overlayRef.attach(portal);
  // }

  // public closeSpinner() {
  //   this.overlayRef.detach();
  //   this.overlayRef.dispose();
  // }

}
